// Two-step login (admin PR 2), pure helpers for the screens: the QR code drawn in
// the browser (the secret never goes to any outside service), what the code box
// accepts, and the backup-codes text to copy / download.

/** Dark modules of a QR code as one SVG path (1 unit per module, `margin` modules of quiet zone). */
export function qrPath(dark: (row: number, col: number) => boolean, size: number, margin = 4): string {
  let d = '';
  for (let r = 0; r < size; r++) {
    let c = 0;
    while (c < size) {
      if (!dark(r, c)) {
        c++;
        continue;
      }
      // one rectangle per run of dark modules in a row (smaller path)
      let end = c;
      while (end < size && dark(r, end)) end++;
      d += `M${c + margin} ${r + margin}h${end - c}v1h-${end - c}z`;
      c = end;
    }
  }
  return d;
}

/** The QR code of `text` (loaded only on the setup screens): its size in modules and the SVG path. */
export async function qrCode(text: string): Promise<{ size: number; path: string; viewBox: number }> {
  const { default: qrcode } = await import('qrcode-generator');
  const qr = qrcode(0, 'M');
  qr.addData(text, 'Byte');
  qr.make();
  const size = qr.getModuleCount();
  const margin = 4;
  return { size, path: qrPath((r, c) => qr.isDark(r, c), size, margin), viewBox: size + margin * 2 };
}

/** What the 6-digit box keeps while typing: digits only, at most 6. */
export function cleanAppCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

/** A backup code as typed ("K7QM X2PD" → "k7qm-x2pd"); null when it can't be one. */
export function cleanBackupCode(raw: string): string | null {
  const b = raw.toLowerCase().replace(/[\s-]/g, '');
  return /^[a-hjkmnp-z2-9]{8}$/.test(b) ? `${b.slice(0, 4)}-${b.slice(4)}` : null;
}

/** The backup codes as a text file / clipboard text. */
export function backupCodesText(codes: string[], account: string, when: Date = new Date()): string {
  const day = when.toISOString().slice(0, 10);
  return [
    `CloudPhone backup codes for ${account} (made ${day})`,
    '',
    'Each code logs you in once if you can’t use your authenticator app.',
    'Keep them somewhere safe, like a password manager. Making new codes stops these.',
    '',
    ...codes.map((c, i) => `${String(i + 1).padStart(2, ' ')}. ${c}`),
    '',
  ].join('\n');
}

export const backupFileName = (when: Date = new Date()) => `cloudphone-backup-codes-${when.toISOString().slice(0, 10)}.txt`;
